import { fetchQuery } from 'react-relay';

import logger from 'src/util/logger';

/**
 * Helper function to merge data from multiple portfolio data chunks
 * This function makes the deep cloning and merging logic testable
 * @param {object} initialData - Initial portfolio data to use as the base
 * @param {Array} chunks - Array of data chunks to merge
 * @returns {object} Merged portfolio data
 */
export const mergePortfolioDataChunks = (initialData, chunks) => {
  if (!initialData || !chunks || !chunks.length) {
    logger.error('No initialData or chunks provided to merge');
    return initialData;
  }

  logger.log(`Merging ${chunks.length} chunks`);

  try {
    // Detailed logging of chunk data to help debug
    chunks.forEach((chunk, idx) => {
      const hasPortfolio = !!chunk?.portfolio;
      const hasMeters = !!chunk?.portfolio?.meters?.edges;
      const meterCount = chunk?.portfolio?.meters?.edges?.length || 0;
      const hasTradeSummary = !!chunk?.portfolio?.tradeSetSummary;
      const tradeSummaryCount = Array.isArray(chunk?.portfolio?.tradeSetSummary)
        ? chunk.portfolio.tradeSetSummary.length
        : 0;

      logger.log(`Chunk ${idx + 1}: hasPortfolio=${hasPortfolio}, hasMeters=${hasMeters}, meterCount=${meterCount}, hasTradeSummary=${hasTradeSummary}, tradeSummaryCount=${tradeSummaryCount}`);
    });

    // Deep clone initialData to use as the base for merging
    // This ensures we don't modify the original and the object is extensible
    let mergedData;
    try {
      // First create a simple object with just the portfolio
      mergedData = {
        portfolio: JSON.parse(JSON.stringify(initialData?.portfolio || {})),
      };

      if (mergedData?.portfolio) {
        // Initialize the meters edges array if it doesn't exist
        if (!mergedData.portfolio.meters) {
          mergedData.portfolio.meters = { edges: [] };
        }

        // Initialize tradeSetSummary as an empty array
        if (!mergedData.portfolio.tradeSetSummary) {
          mergedData.portfolio.tradeSetSummary = [];
        }
      }

      logger.log('Successfully created base merged data from initialData');
    } catch (cloneError) {
      logger.error('Error cloning initialData:', cloneError);
      // Fallback to using the first chunk as base
      mergedData = JSON.parse(JSON.stringify(chunks[0]));
      logger.log('Falling back to using first chunk as base');
    }

    // Create map of meters by UUID for efficient lookups
    const metersMap = new Map();

    // Now merge all chunks
    chunks.forEach((chunk) => {
      if (!chunk?.portfolio) return;

      // Merge meter data
      if (chunk.portfolio.meters?.edges) {
        chunk.portfolio.meters.edges.forEach((chunkEdge) => {
          const chunkMeterNode = chunkEdge?.node;
          if (!chunkMeterNode || !chunkMeterNode.uuid) return;

          // Check if this meter already exists in our map
          if (!metersMap.has(chunkMeterNode.uuid)) {
            // This is a new meter, add it to our map with empty data arrays
            const meterCopy = JSON.parse(JSON.stringify(chunkMeterNode));

            // Initialize data arrays
            if (meterCopy?.dataConsumed) {
              meterCopy.dataConsumed.data = [];
            }
            if (meterCopy?.dataGenerated) {
              meterCopy.dataGenerated.data = [];
            }

            metersMap.set(chunkMeterNode?.uuid, meterCopy);

            // Also add it to the merged data structure
            mergedData?.portfolio?.meters?.edges.push({
              node: meterCopy,
            });
          }

          // Get the meter from our map
          const mergedMeterNode = metersMap.get(chunkMeterNode?.uuid);

          // Merge dataConsumed
          if (chunkMeterNode.dataConsumed?.data) {
            // Ensure dataConsumed structure exists in merged data
            if (!mergedMeterNode.dataConsumed) {
              mergedMeterNode.dataConsumed = {
                ...chunkMeterNode.dataConsumed,
                data: [],
              };
            }

            // Add chunk data to merged data
            const newData = Array.isArray(chunkMeterNode?.dataConsumed?.data)
              ? chunkMeterNode.dataConsumed.data
              : [];

            if (newData.length > 0) {
              mergedMeterNode.dataConsumed.data.push(...newData);
            }
          }

          // Merge dataGenerated
          if (chunkMeterNode.dataGenerated?.data) {
            // Ensure dataGenerated structure exists in merged data
            if (!mergedMeterNode.dataGenerated) {
              mergedMeterNode.dataGenerated = {
                ...chunkMeterNode.dataGenerated,
                data: [],
              };
            }

            // Add chunk data to merged data
            const newData = Array.isArray(chunkMeterNode?.dataGenerated?.data)
              ? chunkMeterNode.dataGenerated.data
              : [];

            if (newData.length > 0) {
              mergedMeterNode.dataGenerated.data.push(...newData);
            }
          }
        });
      }

      // Merge tradeSetSummary data
      if (Array.isArray(chunk?.portfolio?.tradeSetSummary)) {
        // Create a map to deduplicate trades with the same key
        // (tradePointId, ruleId, direction, type)
        const tradeMap = new Map();

        // First, add existing trades from merged data to our map
        mergedData?.portfolio?.tradeSetSummary?.forEach((trade) => {
          if (!trade?.key) return;

          // Create a unique key based on all key properties
          const {
            tradePointId, ruleId, direction, type,
          } = trade.key || {};
          const mapKey = `${tradePointId || ''}-${ruleId || ''}-${direction || ''}-${type || ''}`;

          if (!tradeMap.has(mapKey)) {
            tradeMap.set(mapKey, trade);
          } else {
            // If this trade already exists, merge the data arrays
            const existingTrade = tradeMap.get(mapKey);
            if (Array.isArray(existingTrade.data) && Array.isArray(trade.data)) {
              // Combine data arrays and remove duplicates based on timestamp
              const allData = [...existingTrade.data, ...trade.data];
              const uniqueData = [];
              const seenTimestamps = new Set();

              allData.forEach((item) => {
                if (!item?.range?.finish) return;
                const timestamp = item.range.finish;
                if (!seenTimestamps.has(timestamp)) {
                  seenTimestamps.add(timestamp);
                  uniqueData.push(item);
                }
              });

              existingTrade.data = uniqueData;
            }
          }
        });

        // Now process the current chunk's trades
        const tradesToAdd = JSON.parse(JSON.stringify(chunk?.portfolio?.tradeSetSummary));
        tradesToAdd?.forEach((trade) => {
          if (!trade?.key) return;

          // Create a unique key based on all key properties
          const {
            tradePointId, ruleId, direction, type,
          } = trade.key || {};
          const mapKey = `${tradePointId || ''}-${ruleId || ''}-${direction || ''}-${type || ''}`;

          if (!tradeMap.has(mapKey)) {
            // This is a new trade we haven't seen before
            tradeMap.set(mapKey, trade);
          } else {
            // This trade exists, merge the data arrays
            const existingTrade = tradeMap.get(mapKey);
            if (Array.isArray(existingTrade?.data) && Array.isArray(trade?.data)) {
              // Combine data arrays and remove duplicates based on timestamp
              const allData = [...existingTrade.data, ...trade.data];
              const uniqueData = [];
              const seenTimestamps = new Set();

              allData?.forEach((item) => {
                if (!item?.range?.finish) return;
                const timestamp = item.range.finish;
                if (!seenTimestamps.has(timestamp)) {
                  seenTimestamps.add(timestamp);
                  uniqueData.push(item);
                }
              });

              existingTrade.data = uniqueData;
            }
          }
        });

        // Replace the tradeSetSummary with our merged, deduplicated trades
        mergedData.portfolio.tradeSetSummary = Array.from(tradeMap.values());
      }
    }); // Close the chunks.forEach

    // Sort the merged tradeSetSummary by timestamp
    // This is important because the graphql endpoints return data in timestamp order
    if (mergedData?.portfolio?.tradeSetSummary) {
      // First, create a map of trades by timestamp to ensure uniqueness
      const tradesByTimestamp = new Map();

      // Process all trades
      mergedData.portfolio.tradeSetSummary.forEach((trade) => {
        if (!trade.data || !Array.isArray(trade.data) || trade.data.length === 0) return;

        // Use the first data item's finish timestamp as the primary key
        const timestamp = trade.data[0]?.range?.finish;
        if (!timestamp) return;

        // Store the trade in our map, using timestamp as key
        // If multiple trades have the same timestamp, we store all of them
        if (!tradesByTimestamp.has(timestamp)) {
          tradesByTimestamp.set(timestamp, []);
        }
        tradesByTimestamp.get(timestamp).push(trade);
      });

      // Sort timestamps chronologically
      const sortedTimestamps = Array.from(tradesByTimestamp.keys()).sort();

      // Create a new tradeSetSummary array with trades ordered by timestamp
      const sortedTrades = [];
      sortedTimestamps?.forEach((ts) => {
        const tradesForTimestamp = tradesByTimestamp.get(ts) || [];
        sortedTrades.push(...tradesForTimestamp);
      });

      // Replace the original tradeSetSummary with our sorted version
      mergedData.portfolio.tradeSetSummary = sortedTrades;

      logger.log(`Sorted ${sortedTrades.length} trades by timestamp`);
    }

    return mergedData;
  } catch (error) {
    logger.error('Error merging data chunks:', error);
    return chunks[0]; // Fallback to just using the first chunk
  }
};

/**
 * Helper function to get the start of the next day
 * @param {object} date - Luxon DateTime object
 * @param {string} [timezone] - Timezone to use for the operation (optional)
 * @returns {object} First moment of the next day in the specified timezone
 *                   (or date's timezone if not provided)
 */
export const startOfNextDay = (date, timezone) => {
  // If timezone is provided, ensure the date is in that timezone before operations
  const dateInTz = timezone ? date.setZone(timezone) : date;
  return dateInTz.plus({ days: 1 }).startOf('day').setZone(dateInTz.zone);
};

/**
 * Split time range into chunks of specified size
 * @param {object} startTime - Start time (Luxon DateTime)
 * @param {object} finishTime - Finish time (Luxon DateTime)
 * @param {string} [timezone] - Timezone to use for the operations (optional)
 * @param {number} [chunkSize =4] - Size of each chunk in hours
 * @returns {Array} Array of chunks with start and finish times
 */
export const splitTimeRangeIntoChunks = (startTime, finishTime, timezone, chunkSize = 4) => {
  const chunks = [];

  // Ensure the times are in the specified timezone
  const startTimeInTz = startTime.setZone(timezone);
  const finishTimeInTz = finishTime.setZone(timezone);

  let currentStart = startTimeInTz;
  const end = finishTimeInTz;

  while (currentStart < end) {
    // DST-aware chunk calculation:
    // For 24-hour chunks (full calendar days), use calendar-based arithmetic to handle
    // DST transitions correctly. During spring forward, adding 24 hours of duration
    // can result in the wrong local time (e.g., 1:00 AM instead of midnight).
    // Calendar-based arithmetic maintains the same local time across DST boundaries.
    const chunkEnd = chunkSize === 24
      ? currentStart.plus({ days: 1 })
      : currentStart.plus({ hours: chunkSize });

    let actualEnd;
    if (chunkEnd.diff(end).milliseconds > 1) {
      // If the chunk would exceed the end time by more than 1 millisecond, use the end time
      // This is to ensure we fetch the last time period since the date picker needs the time
      // set to the end of the day to reflect the correct time period
      actualEnd = end;
    } else {
      // Normal case - use the calculated chunk end
      actualEnd = chunkEnd;
    }

    chunks.push({
      start: currentStart,
      finish: actualEnd,
    });

    currentStart = actualEnd;
  }

  logger.log(`Created ${chunks.length} chunks for data fetching`);
  return chunks;
};

/**
 * Helper function to fetch a single portfolio data chunk using Relay
 * @param {object} options - Options object for the fetch
 * @param {string} options.uuid - Portfolio UUID
 * @param {object} options.chunkStart - Start time (Luxon DateTime)
 * @param {object} options.chunkEnd - End time (Luxon DateTime)
 * @param {string} options.aggregation - Aggregation granularity (e.g., 'PT30M', 'P1D')
 * @param {string} options.timezone - Timezone to use for the data
 * @param {object} options.environment - Relay environment
 * @param {object} options.query - GraphQL query object
 * @param {Function} options.onChunkLoaded - Callback when a chunk is loaded
 * @returns {Promise<object>} Promise that resolves to the chunk data or null if failed
 */
export const fetchPortfolioDataChunk = (options) => {
  const {
    uuid,
    chunkStart,
    chunkEnd,
    aggregation,
    timezone,
    environment,
    query,
    onChunkLoaded,
  } = options;

  return new Promise((resolve) => {
    try {
      const variables = {
        uuid,
        start: Math.floor(chunkStart.toSeconds()),
        finish: Math.floor(chunkEnd.toSeconds()),
        timeZone: timezone,
        aggregation,
      };

      logger.log(`Fetching chunk: ${chunkStart.toISO()} to ${chunkEnd.toISO()}`);

      // Create a timeout to handle stuck requests
      const timeoutId = setTimeout(() => {
        console.warn(`Chunk fetch timed out: ${chunkStart.toISO()} to ${chunkEnd.toISO()}`);
        resolve(null); // Resolve with null to continue with other chunks
      }, 240000); // 240 second timeout

      // fetchQuery is now imported at the top of the file

      fetchQuery(environment, query, variables).subscribe({
        next: (data) => {
          clearTimeout(timeoutId);

          // Notify that a chunk has been loaded
          if (typeof onChunkLoaded === 'function') {
            onChunkLoaded();
          }

          // Validate the returned data has what we need
          if (!data || !data.portfolio) {
            console.warn(`Chunk returned invalid data: ${chunkStart.toISO()} to ${chunkEnd.toISO()}`);
            resolve(null);
            return;
          }

          // Check if the chunk has the expected fields
          const hasMeterData = data.portfolio.meters?.edges?.length > 0;
          const hasTradeSummary = Array.isArray(data.portfolio.tradeSetSummary)
            && data.portfolio.tradeSetSummary.length > 0;

          logger.log(`Chunk ${chunkStart.toISO()} to ${chunkEnd.toISO()} has: meters=${hasMeterData}, tradeSummary=${hasTradeSummary}`);

          // Add a chunk identifier to help with debugging
          const augmentedData = {
            ...data,
            _chunkInfo: {
              start: chunkStart.toISO(),
              end: chunkEnd.toISO(),
              timezoneName: timezone,
              aggregation,
            },
          };

          resolve(augmentedData);
        },
        error: (error) => {
          clearTimeout(timeoutId);
          logger.error(`Error fetching chunk ${chunkStart.toISO()} to ${chunkEnd.toISO()}:`, error);
          resolve(null); // Resolve with null to continue with other chunks
        },
        complete: () => {
          // Query completed successfully
        },
      });

      // We shouldn't immediately clean up the subscription - it needs to run
      // The subscription will be automatically disposed of when the promise resolves or rejects
    } catch (err) {
      logger.error('Error setting up chunk fetch', err);
      resolve(null); // Resolve with null to continue with other chunks
    }
  });
};

/**
 * Fetches portfolio data in chunks, handles merging, and manages loading state
 * @param {object} options - Options object for the fetch operation
 * @param {string} options.portfolioId - ID of the portfolio to fetch
 * @param {object} options.dateSpan - Date span with start and finish properties (Luxon DateTimes)
 * @param {string} options.timezone - Timezone to use for data
 * @param {object} options.environment - Relay environment for GraphQL queries
 * @param {object} options.query - GraphQL query to use for fetching chunks
 * @param {object} options.initialData - Initial data to merge chunks with
 * @param {Function} options.onLoadingChange - Callback when loading state changes
 * @param {Function} options.onTotalChunksChange - Callback when total chunks is determined
 * @param {Function} options.onChunkLoaded - Callback when a chunk is loaded
 * @param {Function} options.onDataReceived - Callback when all data is received and merged
 * @param {Function} options.onError - Callback when an error occurs
 * @param {Function} options.mergeFunction - Function to merge chunks
 *                                          (defaults to mergePortfolioDataChunks)
 * @param {object} options.isFetchingRef - React ref to track if a fetch is in progress
 * @param {Function} options.onTimeRangeUpdated - Callback for time range updates
 * @param {number} options.chunkSizeHours - Size of each chunk in hours (e.g., 4, 24)
 * @param {string} options.aggregation - Aggregation to use for both historian and trade data
 *                                    (e.g., 'PT30M', 'P1D')
 * @returns {void}
 */
export const fetchPortfolioDataInChunks = (options) => {
  const {
    portfolioId,
    dateSpan,
    timezone,
    environment,
    query,
    initialData,
    onLoadingChange,
    onTotalChunksChange,
    onChunkLoaded,
    onDataReceived,
    onError,
    mergeFunction = mergePortfolioDataChunks,
    isFetchingRef,
    onTimeRangeUpdated,
    chunkSizeHours,
    aggregation,
  } = options;

  // Safety check for required parameters
  if (!portfolioId || !dateSpan || !timezone || !environment || !query) {
    if (onError) {
      onError(new Error('Missing required parameters for fetchDataInChunks'));
    }
    return;
  }

  // Prevent parallel fetches
  if (isFetchingRef && isFetchingRef.current) {
    logger.log('Skipping fetch - another fetch is already in progress');
    return;
  }

  // Set fetching flag if available
  if (isFetchingRef) {
    isFetchingRef.current = true;
  }

  // Notify loading has started
  if (onLoadingChange) {
    onLoadingChange(true);
  }

  const { start: startTime, finish: finishTime } = dateSpan || {};
  const timespanDuration = finishTime.diff(startTime, 'days').days;

  logger.log(
    'Fetching data for time range:',
    startTime.toISO(),
    'to',
    finishTime.toISO(),
    `(${timespanDuration} days)`,
  );

  logger.log(`Using aggregation: ${aggregation}`);
  logger.log(`Using chunk size: ${chunkSizeHours} hours`);

  // Split the time range into chunks
  const timeChunks = splitTimeRangeIntoChunks(startTime, finishTime, timezone, chunkSizeHours);

  // Notify of total chunks if callback provided
  if (onTotalChunksChange) {
    onTotalChunksChange(timeChunks.length);
  }

  // Reset chunks loaded counter if callback provided
  if (onChunkLoaded) {
    // Initial reset to 0
    onChunkLoaded(0, timeChunks.length);
  }

  // Track promises for all chunk fetches using the fetchPortfolioChunk function
  const fetchPromises = timeChunks.map((chunk) => (
    fetchPortfolioDataChunk({
      uuid: portfolioId,
      chunkStart: chunk.start,
      chunkEnd: chunk.finish,
      aggregation,
      timezone,
      environment,
      query,
      onChunkLoaded: () => {
        if (onChunkLoaded) {
          onChunkLoaded(1, timeChunks.length); // Increment by 1
        }
      },
    })
  ));

  // Log chunk information before fetching
  logger.log(
    `Starting fetch of ${timeChunks.length} chunks with time ranges:`,
    timeChunks.map((chunk) => `${chunk.start.toISO()} to ${chunk.finish.toISO()}`),
  );

  // Process all chunk results
  Promise.all(fetchPromises)
    .then((results) => {
      try {
        if (!results || results.length === 0) {
          throw new Error('No results returned from fetch promises');
        }

        // Log all results for debugging (even null ones)
        results.forEach((result, idx) => {
          if (result) {
            logger.log(`Chunk ${idx + 1}: Successfully fetched data for chunk`);
          } else {
            console.warn(`Chunk ${idx + 1}: Returned null data (failed fetch)`);
          }
        });

        const validResults = results.filter(Boolean);
        logger.log(`Received ${validResults.length} valid chunks out of ${fetchPromises.length} requested`);

        if (validResults.length > 0) {
          try {
            // If we have at least one chunk with data, try to merge
            const mergedData = mergeFunction(initialData, validResults);

            if (mergedData && mergedData.portfolio) {
              logger.log('Successfully merged portfolio data');

              // Notify of data received if callback provided
              if (onDataReceived) {
                onDataReceived(mergedData, validResults);
              }

              // Notify of time range update if callback provided
              if (onTimeRangeUpdated) {
                onTimeRangeUpdated(startTime, finishTime);
              }
            } else {
              throw new Error('Merged data is incomplete or invalid');
            }
          } catch (mergeError) {
            logger.error('Error merging data chunks:', mergeError);

            if (onError) {
              onError(mergeError, 'Error processing data. Try a smaller date range.');
            }

            // Fallback to using just the first valid chunk if merge fails
            if (validResults[0] && onDataReceived) {
              logger.log('Fallback: Using just the first chunk');
              onDataReceived(validResults[0], [validResults[0]]);
            }
          }
        } else if (onError) {
          onError(new Error('No valid data'), 'No data was returned for the selected time period');
        }
      } catch (processingError) {
        logger.error('Error processing results:', processingError);

        if (onError) {
          onError(processingError, 'Error processing results');
        }
      } finally {
        // Clear fetching flag if available
        if (isFetchingRef) {
          isFetchingRef.current = false;
        }
      }
    })
    .catch((error) => {
      logger.error('Error fetching data:', error);

      if (onError) {
        onError(error, 'Error fetching data');
      }

      if (onLoadingChange) {
        onLoadingChange(false);
      }

      if (isFetchingRef) {
        isFetchingRef.current = false;
      }
    });
};
